import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Import GTM v2 slide components
import {
  GTMv2Slide01Title,
  GTMv2Slide02MarketOpportunity,
  GTMv2Slide03DualStrategy,
  GTMv2Slide04ThreeYearRoadmap,
  GTMv2Slide05FinancialCalculator,
  GTMv2Slide06ThankYou,
  GTMv2Slide07SchoolMap,
} from '../components/gtm-slides-v2';
import { FinancialModelProvider } from '../components/gtm-slides-v2/FinancialModelContext';

const slides = [
  { component: GTMv2Slide01Title, title: 'Title' },
  { component: GTMv2Slide02MarketOpportunity, title: 'Market Opportunity' },
  { component: GTMv2Slide03DualStrategy, title: 'Dual GTM Strategy' },
  { component: GTMv2Slide04ThreeYearRoadmap, title: '3-Year Roadmap' },
  { component: GTMv2Slide05FinancialCalculator, title: 'Financial Calculator' },
  { component: GTMv2Slide06ThankYou, title: 'Thank You' },
  { component: GTMv2Slide07SchoolMap, title: 'NSW Schools Map' },
];

const PresentationGTM: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showOverview, setShowOverview] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate if user is interacting with form elements
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) {
        return;
      }

      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        if (showOverview) {
          setShowOverview(false);
        } else {
          nextSlide();
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!showOverview) {
          prevSlide();
        }
      } else if (e.key === 'Escape') {
        setShowOverview(!showOverview);
      } else if (e.key === 'o' || e.key === 'O') {
        setShowOverview(!showOverview);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, showOverview]);

  const CurrentSlideComponent = slides[currentSlide].component;

  return (
    <FinancialModelProvider>
    <div
      className="fixed inset-0 flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #0a0e27 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      {/* Slide Overview Modal */}
      <AnimatePresence>
        {showOverview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0, 0, 0, 0.9)' }}
            onClick={() => setShowOverview(false)}
          >
            <div className="max-w-4xl max-h-[80vh] overflow-auto p-8">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">
                Go-To-Market Strategy - Slide Overview
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {slides.map((slide, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentSlide(index);
                      setShowOverview(false);
                    }}
                    className={`p-4 rounded-lg text-left transition-all ${
                      index === currentSlide
                        ? 'bg-[#10b981]/30 border-2 border-[#10b981]'
                        : 'bg-[#1e293b] border border-[#334155] hover:border-[#10b981]/50'
                    }`}
                  >
                    <div className="text-xs text-[#64748b] mb-1">Slide {index + 1}</div>
                    <div className="text-sm text-white font-medium">{slide.title}</div>
                  </button>
                ))}
              </div>
              <p className="text-center text-[#64748b] text-sm mt-6">
                Press ESC or O to close • Click a slide to jump
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main slide area */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <CurrentSlideComponent />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation bar */}
      <div
        className="flex items-center justify-between px-8 py-4"
        style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Left: Logo */}
        <div className="flex items-center gap-4">
          <span className="text-xl font-black bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent">
            Lumen8
          </span>
          <span className="text-sm text-[#64748b]">Go-To-Market Strategy</span>
        </div>

        {/* Center: Progress dots */}
        <div className="flex items-center gap-1.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'w-8 bg-[#10b981]'
                  : 'w-2 bg-[#334155] hover:bg-[#475569]'
              }`}
              title={slides[index].title}
            />
          ))}
        </div>

        {/* Right: Navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowOverview(true)}
            className="text-sm text-[#64748b] hover:text-white transition-colors"
          >
            Overview (O)
          </button>
          <span className="text-sm text-[#64748b]">
            {currentSlide + 1} / {slides.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="p-2 rounded-lg bg-[#1e293b] hover:bg-[#334155] transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="p-2 rounded-lg bg-[#1e293b] hover:bg-[#334155] transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
    </FinancialModelProvider>
  );
};

export default PresentationGTM;
